import { AuthenticationService } from 'src/app/core/services/auth.service';
import { BooleanService } from 'src/app/core/services/utelize/general/boolean-service';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { ConfigSystemParams, DgColumnDataType, DgColumnWidths, eSimApiPaths } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { GridViewDefaults } from 'src/app/core/grid-view/component/grid-view.defaults';
import { DgActionColumn } from 'src/app/core/interfaces/datagrid/dg-action-column';
import { DgApiPath } from 'src/app/core/interfaces/datagrid/dg-api-paths';
import { DgColumn } from 'src/app/core/interfaces/datagrid/dg-column';
import { DgPopup } from 'src/app/core/interfaces/datagrid/dg-pop-up';
import { DgProperties } from 'src/app/core/interfaces/datagrid/dg-properties';
import { EncryptionService } from 'src/app/core/services/utelize/general/encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { firstValueFrom } from 'rxjs';
import { GridViewComponent } from 'src/app/core/grid-view/component/grid-view.component';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';

const apiPath             =environment.eSimApi+eSimApiPaths.Orders;
const exportFilenameSuffix="SIM-Product-Change-Log-Data";
const popUpTitle          ="SIM Product Change Log";

@Component({
  selector: 'app-sim-product-change-log-embedded',
  templateUrl: './sim-product-change-log.component.html',
  styleUrls: ['./sim-product-change-log.component.scss']
})

export class SimProductChangeLogEmbeddedComponent implements OnInit {
  
  @Input() iccid  : string | undefined;
  @Input() orderNo: string | undefined;
  
  @ViewChild('gridView', { static: false }) gridView!: GridViewComponent;
  
  private gridViewDefaults!: GridViewDefaults;
  
  public curUser        : SystemUserDTO;
  public dgApiPath!     : DgApiPath | undefined;
  public dgActionColumn!: DgActionColumn | undefined;
  public dgColumns!     : Array<DgColumn>;
  public dgPopup!       : DgPopup | undefined;
  public dgProperties!  : DgProperties | undefined;
  public gotDgProperties: boolean=false;
  
  constructor(
    private authenticationService     : AuthenticationService,
    private booleanService            : BooleanService,
    private componentAccessUserService: ComponentAccessUserService,
    private configSystemService       : ConfigSystemService,
    private encryptionService         : EncryptionService,
    private exceptionsService         : ExceptionsService
  ) {
    this.curUser         =this.authenticationService.currentUserObject();
    this.gridViewDefaults=new GridViewDefaults(this.authenticationService, this.componentAccessUserService, this.configSystemService, this.exceptionsService);
  }

  public async ngOnInit(): Promise<void> {
    try {
      if (this.iccid == undefined) {
        throw new Error("SimProductChangeLogEmbeddedComponent - ICCID cannot be determined");
      }
      
      if (this.orderNo == undefined) {
        throw new Error("SimProductChangeLogEmbeddedComponent - Order No cannot be determined");
      }

      await this.setUpDgApiPaths();
      this.setUpDgActionColumn();
      this.setUpDgColumns();
      this.setUpDgPopup();
      this.setUpDgProperties();

      this.gotDgProperties=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private async setUpDgApiPaths(): Promise<void> {
    try {
      this.dgApiPath            =this.gridViewDefaults.setUpDgApiPaths(apiPath);
      this.dgApiPath!.refreshGet=() => this.setUpDgApiPathGet();

      await this.setUpDgApiPathGet();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async setUpDgApiPathGet(): Promise<void> {
    try {
      const iccidEncResult  =await firstValueFrom(this.encryptionService.encrypt_AesServerSideForWeb(<string>this.iccid, true));
      const orderNoEncResult=await firstValueFrom(this.encryptionService.encrypt_AesServerSideForWeb(<string>this.orderNo));
      const iccidEncSS      =iccidEncResult.result;
      const orderNoEnc      =orderNoEncResult.result;

      this.dgApiPath!.get=environment.eSimApi +
                          eSimApiPaths.Orders + '/' +
                          eSimApiPaths.OrdersGetHistoricProducts +
                          "?orderNoEnc=" + orderNoEnc +
                          "&iccidEncSS=" + iccidEncSS;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgActionColumn() {
    try {
      this.dgActionColumn         =this.gridViewDefaults.setUpDgActionColumn();
      this.dgActionColumn!.visible=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgPopup() {
    try {
      this.dgPopup=this.gridViewDefaults.setUpDgPopup(popUpTitle);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgProperties() {
    try {
      this.dgProperties               =this.gridViewDefaults.setUpDgProperties("", exportFilenameSuffix + "-" + this.iccid);
      this.dgProperties!.gridKey      ="EmbeddedSimProductChangeLog";
      this.dgProperties!.showAddButton=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgColumns() {
    try {
      let dgColumn: DgColumn | undefined;
      
      //ID
      dgColumn             =this.gridViewDefaults.setUpDgColumn("id", "ID", 0, DgColumnDataType.number);
      dgColumn!.gridVisible=this.configSystemService.getValueAsBoolean(ConfigSystemParams.GridShowId, false);
      dgColumn!.width      =DgColumnWidths.id;
      this.dgColumns       =[dgColumn!];

      //OrderNo
      dgColumn       =this.gridViewDefaults.setUpDgColumn("orderNo", "Order No", "", DgColumnDataType.string);
      dgColumn!.width=DgColumnWidths.orderNo;
      this.dgColumns.push(dgColumn!);

      //Active
      dgColumn                    =this.gridViewDefaults.setUpDgColumn("active", "Is Active", true, DgColumnDataType.boolean);
      dgColumn!.dataSource        =this.booleanService.idAndDescriptionLookupDataSource();
      dgColumn!.dataSourceForAForm=this.booleanService.getIdAndDescription();
      dgColumn!.width             =DgColumnWidths.active;
      this.dgColumns.push(dgColumn!);

      //InternalProductName
      dgColumn=this.gridViewDefaults.setUpDgColumn("internalProductName", "Product Name", "", DgColumnDataType.string);
      this.dgColumns.push(dgColumn!);

      //StartDate
      dgColumn       =this.gridViewDefaults.setUpDgColumn("startDate", "Start Date", "", DgColumnDataType.datetime);
      dgColumn!.width=DgColumnWidths.dateWithTime;
      this.dgColumns.push(dgColumn!);

      //EndDate
      dgColumn       =this.gridViewDefaults.setUpDgColumn("endDate", "End Date", "", DgColumnDataType.datetime);
      dgColumn!.width=DgColumnWidths.dateWithTime;
      this.dgColumns.push(dgColumn!);

      //EnteredDate
      dgColumn       =this.gridViewDefaults.setUpDgColumn("enteredDate", "Entered Date", "", DgColumnDataType.datetime);
      dgColumn!.width=DgColumnWidths.dateWithTime;
      this.dgColumns.push(dgColumn!);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
